import { Link } from 'react-router-dom'

import { fetchFips, fetchOrganizations, fetchStats } from '@/api'
import { FipCard } from '@/components/cards'
import {
    Bar,
    Chip,
    ErrorState,
    Hero,
    Loading,
    PageShell,
    Section,
    StatCard,
} from '@/components/common'
import { useAsync } from '@/hooks'
import { useI18n } from '@/i18n'
import { FIP_WIZARD_URL } from '@/links'

export function Overview() {
    const { t, localized } = useI18n()
    const stats = useAsync(fetchStats, [])
    const fips = useAsync(fetchFips, [])
    const organizations = useAsync(fetchOrganizations, [])

    const hero = (
        <Hero
            eyebrow={t('app.owner')}
            title={t('overview.heroTitle')}
            lead={t('overview.lead')}
            actions={
                <>
                    <Link className="button button--light" to="/matrix">
                        {t('nav.matrix')}
                    </Link>
                    <Link className="button button--ghost" to="/resources">
                        {t('overview.browseResources')}
                    </Link>
                    <a
                        className="button button--ghost"
                        href={FIP_WIZARD_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        title={t('overview.authorHint')}
                    >
                        {t('overview.author')} ↗
                    </a>
                </>
            }
        />
    )

    if (stats.error) {
        return (
            <>
                {hero}
                <PageShell>
                    <ErrorState error={stats.error} onRetry={stats.reload} />
                </PageShell>
            </>
        )
    }
    if (!stats.data) {
        return (
            <>
                {hero}
                <PageShell>
                    <Loading />
                </PageShell>
            </>
        )
    }

    const data = stats.data
    const communities = new Map((organizations.data ?? []).map((item) => [item.id, item]))
    const maxTypeCount = Math.max(...data.by_type.map((item) => item.count), 1)
    const maxVerifierCount = Math.max(...data.by_verifier.map((item) => item.count), 1)

    return (
        <>
            {hero}
            <PageShell>
                <div className="grid grid--stats">
                    <StatCard value={data.fips} label={t('overview.stat.fips')} />
                    <StatCard value={data.resources} label={t('overview.stat.resources')} />
                    <StatCard value={data.declarations} label={t('overview.stat.declarations')} />
                    <StatCard
                        value={`${data.verified_resources}/${data.resources}`}
                        label={t('verification.stat')}
                    />
                </div>

                <div className="grid grid--two section">
                    <div className="card">
                        <h2>{t('overview.coverage')}</h2>
                        <p className="section__hint" style={{ marginBottom: '1rem' }}>
                            {t('overview.coverageHint')}
                        </p>
                        <div className="bars">
                            {data.coverage.map((item) => (
                                <Bar
                                    key={item.principle}
                                    label={item.principle}
                                    value={item.fips_covered}
                                    max={item.fips_total}
                                    group={item.group}
                                    caption={`${item.fips_covered}/${item.fips_total}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="card">
                        <h2>{t('overview.types')}</h2>
                        <p className="section__hint" style={{ marginBottom: '1rem' }}>
                            {t('resources.lead')}
                        </p>
                        <div className="bars">
                            {data.by_type.map((item) => (
                                <Bar
                                    key={item.key}
                                    label={localized(item.label)}
                                    value={item.count}
                                    max={maxTypeCount}
                                    wide
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <Section
                    title={t('overview.top')}
                    hint={t('overview.topHint')}
                    action={<Link to="/matrix">{t('nav.matrix')} →</Link>}
                >
                    <div className="card">
                        <div className="bars">
                            {data.top_resources.map((item) => (
                                <div key={item.resource} className="bar bar--neutral bar--wide">
                                    <Link className="bar__label" to={`/resources/${item.resource}`}>
                                        {item.name}
                                    </Link>
                                    <span className="bar__track">
                                        <span
                                            className="bar__fill"
                                            style={{
                                                width: `${(item.usage_count / data.fips) * 100}%`,
                                            }}
                                        />
                                    </span>
                                    <span className="bar__value">
                                        {item.usage_count}/{data.fips}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>

                <Section title={t('verification.verifiers')} hint={t('verification.verifiersHint')}>
                    <div className="card">
                        <div className="bars">
                            {data.by_verifier.map((item) => (
                                <Bar
                                    key={item.key}
                                    label={localized(item.label)}
                                    value={item.count}
                                    max={maxVerifierCount}
                                    wide
                                />
                            ))}
                        </div>
                    </div>
                </Section>

                <Section
                    title={t('overview.statuses')}
                    hint={t('overview.statusesHint')}
                    action={<Link to="/resources">{t('nav.resources')} →</Link>}
                >
                    <div className="chips">
                        {data.by_status.map((item) => (
                            <Chip key={item.key} variant={item.key}>
                                {localized(item.label)} · {item.count}
                            </Chip>
                        ))}
                    </div>
                </Section>

                <Section title={t('overview.profiles')} hint={t('fips.lead')}>
                    {fips.error ? (
                        <ErrorState error={fips.error} onRetry={fips.reload} />
                    ) : (
                        <div className="grid grid--cards">
                            {(fips.data ?? []).map((fip) => (
                                <FipCard
                                    key={fip.id}
                                    fip={fip}
                                    community={communities.get(fip.community)}
                                    verifiers={communities}
                                />
                            ))}
                        </div>
                    )}
                </Section>
            </PageShell>
        </>
    )
}
